import { Pagination } from './index';
import { PaymentMethod } from '../utils/paymentMethods';

/** General-ledger types. Amounts are integer paisa (minor units). */

export type AccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
export type NormalBalance = 'DEBIT' | 'CREDIT';
export type ControlModule = 'FEES' | 'FINES' | 'PAYABLES';
export type EntityType = 'proprietor' | 'partnership' | 'company' | 'trust';

export const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  ASSET: 'Assets',
  LIABILITY: 'Liabilities',
  EQUITY: 'Equity',
  INCOME: 'Income',
  EXPENSE: 'Expenses',
};

export const ENTITY_TYPE_OPTIONS: Array<{ value: EntityType; label: string; hint: string }> = [
  { value: 'proprietor', label: 'Sole proprietor', hint: "Owner's capital, drawings and accumulated profit" },
  { value: 'partnership', label: 'Partnership', hint: "Partners' capital, drawings and accumulated profit" },
  { value: 'company', label: 'Private company', hint: 'Share capital and retained earnings' },
  { value: 'trust', label: 'Trust / not-for-profit', hint: 'Trust fund and accumulated surplus' },
];

export type AccountUse = 'fee' | 'expense' | 'income' | 'payment';

export interface LedgerAccount {
  _id: string;
  code: string;
  name: string;
  description: string | null;
  type: AccountType;
  normalBalance: NormalBalance;
  parentCode: string | null;
  isGroup: boolean;
  controlModule: ControlModule | null;
  active: boolean;
  system: boolean;
  depth: number;
  /** Debit-positive net balance. */
  netPaisa: number;
  /** Balance in the account's own normal direction. */
  balancePaisa: number;
  /** Which categories may post here (the server's rule). */
  usableFor: AccountUse[];
}

export interface ChartResponse {
  accounts: LedgerAccount[];
  entityType: EntityType;
}

/** The school's second-person rule, readable by every approval screen. */
export interface ApprovalRule {
  /** The second-person switch. Off: whoever raised something may also decide it. */
  secondPerson: boolean;
  /** Waivers and flat concessions up to this many rupees apply without a second person. */
  approvalLimit: number;
}

export interface FinanceSettings {
  entityType: EntityType;
  requireJournalApproval: boolean;
  /** Waivers and flat concessions up to this many rupees apply without a second person. */
  approvalLimit: number;
  /** Days overdue before a balance may be written off. */
  writeOffAfterDays: number;
  /** Cash/bank account code each payment method lands in. */
  paymentAccounts: Record<PaymentMethod, string>;
}

/** Fees receivable (1130) against the outstanding on challans. */
export interface FeesControl {
  subLedgerPaisa: number;
  ledgerPaisa: number;
  differencePaisa: number;
  /** Advance fees (2210) against what families hold as advance. */
  advances: { subLedgerPaisa: number; ledgerPaisa: number; differencePaisa: number };
  lastRun: { ranAt: string; differencePaisa: number } | null;
}

/** Fines receivable (1135) against what fine letters still say is owed. */
export interface FinesControl {
  subLedgerPaisa: number;
  ledgerPaisa: number;
  differencePaisa: number;
  lastRun: { ranAt: string; differencePaisa: number } | null;
}

export interface LedgerControls { fees: FeesControl; fines: FinesControl }

/** One movement on a cash or bank account, with the voucher behind it. */
export interface CashMovement {
  lineId: string;
  entryId: string;
  entryNo: string;
  entryDate: string;
  memo: string;
  sourceType: string;
  status: JournalStatus;
  isReversal: boolean;
  createdByName: string | null;
  account: { _id: string; code: string; name: string };
  inPaisa: number;
  outPaisa: number;
}

export interface CashBankResponse {
  items: CashMovement[];
  totals: { inPaisa: number; outPaisa: number; netPaisa: number };
  accounts: Array<{ _id: string; code: string; name: string; balancePaisa: number }>;
  pagination: Pagination;
}

/** Every movement the Cash & bank filters match, for the PDF — with the balances around them and the words for what was filtered. */
export interface CashBankReport extends Omit<CashBankResponse, 'accounts' | 'pagination'> {
  openingPaisa: number;
  closingPaisa: number;
  filterSummary: string | null;
}

/** One line of an account's ledger, with the balance after it (debit-positive). */
export interface AccountLedgerLine {
  lineId: string;
  entryId: string;
  entryNo: string;
  entryDate: string;
  memo: string;
  description: string | null;
  sourceType: string;
  status: JournalStatus;
  isReversal: boolean;
  createdByName: string | null;
  debitPaisa: number;
  creditPaisa: number;
  balancePaisa: number;
  /** The account(s) on the other side of the voucher. */
  against: Array<{ code: string; name: string }>;
  /** Under a group account: which account the line is on. */
  account?: { code: string; name: string } | null;
}

export interface AccountLedgerResponse {
  account: { _id: string; code: string; name: string; type: AccountType; normalBalance: NormalBalance; isGroup: boolean; controlModule: ControlModule | null };
  /** Debit-positive. */
  openingPaisa: number;
  closingPaisa: number;
  items: AccountLedgerLine[];
  totals: { debitPaisa: number; creditPaisa: number };
  pagination: Pagination;
}

/** Every line the Ledger filters match, for the PDF — with the words for what was filtered. */
export type AccountLedgerReport = Omit<AccountLedgerResponse, 'pagination'> & { filterSummary: string };

export interface AccountLedgerFilters {
  from?: string | null; to?: string | null; sourceType?: string;
  /** 'in' = debits, 'out' = credits. */
  direction?: 'in' | 'out' | ''; q?: string; page?: number; limit?: number;
}

export interface CashBankFilters {
  from?: string | null; to?: string | null; accountId?: string; sourceType?: string;
  direction?: 'in' | 'out' | ''; q?: string; page?: number; limit?: number;
}

/** Waivers, write-offs, refunds and deposit returns awaiting (or past) approval. */
export type FeeRequestKind = 'WAIVER' | 'WRITE_OFF' | 'REFUND' | 'DEPOSIT_REFUND' | 'DEPOSIT_RETAIN';
export type FeeRequestStatus = 'PENDING' | 'POSTED' | 'REJECTED';

export const FEE_REQUEST_META: Record<FeeRequestKind, { label: string; verb: string; permission: string; paysOut: boolean; hint: string }> = {
  WAIVER:         { label: 'Waiver',             verb: 'Waive',                 permission: 'fee_waivers',   paysOut: false, hint: 'Part of the balance forgiven. Recorded in the books as a concession.' },
  WRITE_OFF:      { label: 'Bad-debt write-off', verb: 'Write off as bad debt', permission: 'fee_writeoffs', paysOut: false, hint: 'The family is not expected to pay. Recorded as a bad debt; can be undone if they pay later.' },
  REFUND:         { label: 'Fee refund',         verb: 'Refund fees',           permission: 'fee_refunds',   paysOut: true,  hint: 'Paid fees returned under the refund policy.' },
  DEPOSIT_REFUND: { label: 'Deposit returned',   verb: 'Return deposit',        permission: 'fee_refunds',   paysOut: true,  hint: 'The security deposit goes back to the family.' },
  DEPOSIT_RETAIN: { label: 'Deposit retained',   verb: 'Retain deposit',        permission: 'fee_refunds',   paysOut: false, hint: 'The school keeps the deposit under its policy. No money moves.' },
};

export interface FeeRequest {
  _id: string;
  kind: FeeRequestKind;
  invoiceId: string;
  amount: number;
  /** Waivers: the challan line forgiven; null = the whole challan. */
  lineItemIndex: number | null;
  method: PaymentMethod | null;
  reference: string | null;
  reason: string;
  status: FeeRequestStatus;
  createdBy: string;
  createdByName: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  rejectionReason: string | null;
  /** Set when a posted waiver or write-off was later undone on the challan. */
  undone: { at: string | null; byName: string | null; reason: string | null } | null;
  createdAt: string;
  invoice: { _id: string; invoiceNumber: string; period?: { label: string } } | null;
  /** `applicationNumber`: the challan's application, for an applicant with no GR yet. */
  person: { _id: string; name: string; grNumber?: string | null; applicationNumber?: string | null } | null;
}

export interface FeeRequestLimits {
  /** What may still be waived: outstanding less waivers and write-offs waiting for approval. */
  waiver: number;
  writeOff: { max: number; eligible: boolean; reason: string };
  refund: number;
  deposit: number;
  /** A second person approves before it posts. */
  requiresApproval: boolean;
  /** Waivers up to this many rupees apply without a second person. */
  approvalLimit: number;
}

export interface TrialBalance {
  asOf: string;
  rows: LedgerAccount[];
  totals: { debitPaisa: number; creditPaisa: number; balanced: boolean };
}

export type FiscalPeriodStatus = 'OPEN' | 'CLOSED' | 'LOCKED';
export type FiscalPeriodAction = 'close' | 'lock' | 'reopen' | 'decline-reopen';

export interface FiscalPeriod {
  _id: string;
  key: string;
  fiscalYear: number;
  startDate: string;
  endDate: string;
  status: FiscalPeriodStatus;
  entries: number;
  history: Array<{ action: 'CLOSE' | 'LOCK' | 'REOPEN'; at: string; byName: string | null; approvedByName: string | null; reason: string | null }>;
  /** A reopen waiting for a second person. */
  reopenRequest: { by: string; byName: string | null; at: string; reason: string } | null;
  /** Closed with a checksum over its lines. */
  sealed: boolean;
  /** The nightly check found the lines changed outside the ledger. */
  tampered: boolean;
  /** Entries posted after the month closed, by someone allowed to. */
  elevatedPostings: Array<{ entryId: string; entryNo: string; at: string; by: string; byName: string | null }>;
}

export interface CloseChecklistItem { key: string; label: string; ok: boolean; detail: string }

export interface YearEndStatus {
  fiscalYear: number;
  closed: boolean;
  entryNo: string | null;
  /** Months in the books still open (months before the first entry don't count). */
  openMonths: number;
  /** The year's last day has passed. */
  ended: boolean;
  canClose: boolean;
  /** Profit (positive) or loss for the year. */
  profitPaisa: number;
}

// ── Cash counts ──────────────────────────────────────────────────────────

export type CashCountStatus = 'AGREED' | 'PENDING' | 'POSTED' | 'REJECTED';

export interface CashCount {
  _id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  day: string;
  expectedPaisa: number;
  countedPaisa: number;
  /** Counted − expected: over (positive) or short. */
  differencePaisa: number;
  denominations: Array<{ value: number; count: number }>;
  note: string | null;
  status: CashCountStatus;
  countedBy: string;
  countedByName: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  rejectionReason: string | null;
  journalEntryId: string | null;
  entryNo: string | null;
  createdAt: string;
}

export interface CashCountInput {
  accountId: string;
  day: string;
  denominations: Array<{ value: number; count: number }>;
  note?: string;
}

// ── Bank reconciliation ──────────────────────────────────────────────────

export interface BankAccountReconciliation {
  accountId: string;
  code: string;
  name: string;
  balancePaisa: number;
  open: { _id: string; statementDate: string; statementBalancePaisa: number } | null;
  lastReconciled: { _id: string; statementDate: string; statementBalancePaisa: number } | null;
  history: Array<{ _id: string; statementDate: string; statementBalancePaisa: number; completedAt: string }>;
}

export interface ReconciliationLine {
  _id: string;
  entryId: string;
  entryDate: string;
  entryNo: string;
  memo: string;
  sourceType: string;
  inPaisa: number;
  outPaisa: number;
  cleared: boolean;
}

export interface Reconciliation {
  _id: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  statementDate: string;
  statementBalancePaisa: number;
  status: 'OPEN' | 'COMPLETED';
  createdByName: string | null;
  completedByName: string | null;
  completedAt: string | null;
  lines: ReconciliationLine[];
  summary: {
    openingPaisa: number;
    clearedInPaisa: number;
    clearedOutPaisa: number;
    clearedBalancePaisa: number;
    statementBalancePaisa: number;
    differencePaisa: number;
    depositsInTransitPaisa: number;
    unpresentedPaymentsPaisa: number;
    bookBalancePaisa: number;
  };
}

// ── Opening balances ─────────────────────────────────────────────────────

export interface OpeningBalances {
  entry: { _id: string; entryNo: string; date: string; revisions: number } | null;
  /** Go-live date: the entry's, or the start of this fiscal year. */
  date: string;
  /** False once the go-live month has closed. */
  editable: boolean;
  /** Balances in each account's normal direction (an asset's debit, a liability's credit). */
  accounts: Array<{ _id: string; code: string; name: string; type: AccountType; normalBalance: NormalBalance; parentCode: string | null; balancePaisa: number }>;
  /** Owner's capital: the balancing figure. */
  capitalPaisa: number;
}

export type JournalStatus = 'POSTED' | 'REVERSED';

export interface JournalEntry {
  _id: string;
  entryNo: string;
  entryDate: string;
  fiscalYear: number;
  sourceType: string;
  sourceId: string | null;
  memo: string;
  totalPaisa: number;
  status: JournalStatus;
  reversesEntryId: string | null;
  reversedByEntryId: string | null;
  reversalReason: string | null;
  createdAt: string;
  createdByName: string | null;
}

export interface JournalLineView {
  _id?: string;
  accountId: string;
  accountCode: string;
  accountName: string;
  debitPaisa: number;
  creditPaisa: number;
  description: string | null;
}

export interface JournalEntryDetail extends JournalEntry {
  approvedByName: string | null;
  reversesEntryNo: string | null;
  reversedByEntryNo: string | null;
  lines: JournalLineView[];
}

export interface JournalListResponse { items: JournalEntry[]; pagination: Pagination }

export interface JournalFilters {
  from?: string | null;
  to?: string | null;
  sourceType?: string;
  accountId?: string;
  status?: JournalStatus | '';
  q?: string;
  page?: number;
  limit?: number;
}

export type ManualJournalStatus = 'PENDING' | 'POSTED' | 'REJECTED';

export interface ManualJournal {
  _id: string;
  entryDate: string;
  memo: string;
  lines: JournalLineView[];
  totalPaisa: number;
  status: ManualJournalStatus;
  createdBy: string;
  createdByName: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  rejectionReason: string | null;
  entryNo: string | null;
  journalEntryId: string | null;
  createdAt: string;
}

export interface ManualJournalListResponse { items: ManualJournal[]; pagination: Pagination }

export interface ManualJournalInput {
  entryDate: string;
  memo: string;
  lines: Array<{ accountId: string; debitPaisa: number; creditPaisa: number; description?: string | null }>;
}

/** Where a journal entry came from, in words. */
export const JOURNAL_SOURCE_LABEL: Record<string, string> = {
  MANUAL_JOURNAL: 'Manual journal',
  FEE_CHALLAN_ISSUED: 'Challan issued',
  FEE_INCOME_RECOGNISED: 'Fee income recognised',
  FEE_PAYMENT_RECEIVED: 'Fee receipt',
  FEE_WAIVED: 'Concession',
  FEE_ADJUSTED: 'Challan adjustment',
  FEE_REFUNDED: 'Refund / deposit',
  EXPENSE_PAID: 'Expense',
  OTHER_INCOME_RECEIVED: 'Other income',
  FINE_ISSUED: 'Fine issued',
  FINE_PAID: 'Fine receipt',
  FINE_REDUCED: 'Fine waived / cancelled',
  CASH_COUNTED: 'Cash count',
  BANK_ADJUSTED: 'Bank charge / profit',
  OPENING_BALANCES: 'Opening balances',
  YEAR_END_CLOSE: 'Year-end close',
};

/** The screen that owns an automatic entry — undo it there, not in the journal. */
export const JOURNAL_SOURCE_HOME: Record<string, { label: string; path: string }> = {
  FEE_CHALLAN_ISSUED: { label: 'Challans', path: '/app/fees/invoices' },
  FEE_PAYMENT_RECEIVED: { label: 'Fee collection', path: '/app/fees/collection' },
  FEE_WAIVED: { label: 'Challans', path: '/app/fees/invoices' },
  FEE_ADJUSTED: { label: 'Challans', path: '/app/fees/invoices' },
  FEE_REFUNDED: { label: 'Approvals', path: '/app/finance/approvals' },
  EXPENSE_PAID: { label: 'Expense approvals', path: '/app/accounts/expense-approvals' },
  OTHER_INCOME_RECEIVED: { label: 'Revenue', path: '/app/accounts/revenues' },
  FINE_ISSUED: { label: 'Fines', path: '/app/accounts/fines' },
  FINE_PAID: { label: 'Fines', path: '/app/accounts/fines' },
  FINE_REDUCED: { label: 'Fines', path: '/app/accounts/fines' },
  CASH_COUNTED: { label: 'Cash & bank', path: '/app/finance/cash-bank?tab=counts' },
  BANK_ADJUSTED: { label: 'Cash & bank', path: '/app/finance/cash-bank?tab=reconcile' },
  OPENING_BALANCES: { label: 'Opening balances', path: '/app/finance/setup?item=opening-balances' },
  YEAR_END_CLOSE: { label: 'Fiscal periods', path: '/app/finance/setup?item=fiscal-periods' },
};

export const JOURNAL_SOURCE_OPTIONS = Object.entries(JOURNAL_SOURCE_LABEL).map(([value, label]) => ({ value, label }));

// ── Financial statements ─────────────────────────────────────────────────

export type StatementKind = 'profit-and-loss' | 'balance-sheet' | 'cash-flow';
export type StatementLineKind = 'heading' | 'group' | 'account' | 'subtotal' | 'total' | 'note';
export type PeriodPreset = 'month' | 'lastMonth' | 'quarter' | 'fiscalYear' | 'lastFiscalYear';

export interface StatementLine {
  kind: StatementLineKind;
  label: string;
  code?: string;
  accountId?: string;
  depth: number;
  /** Paisa, one per column; absent on headings and notes. */
  amounts?: number[];
}

export interface Statement {
  kind: StatementKind;
  title: string;
  columns: Array<{ label: string; from?: string; to?: string }>;
  lines: StatementLine[];
  balanced?: boolean;
  /** The dates the server used (presets resolve on the server, in fiscal terms). */
  range: { from?: string; to: string };
}

export interface StatementQuery {
  period?: PeriodPreset;
  from?: string;
  to?: string;
  asOf?: string;
  compare?: 'previous' | 'lastYear';
}
